import { createClient } from '@supabase/supabase-js';
import { getStoredAttribution } from '../utils/captureAttribution';
import { trackCapiEvent } from '../utils/trackCapiEvent';

// Lazy Supabase client creation - waits for env to be loaded
let _supabase: ReturnType<typeof createClient> | null = null;

function getSupabaseClient() {
  if (!_supabase) {
    const env = (window as any).__ENV__;
    if (!env?.SUPABASE_URL || !env?.SUPABASE_ANON_KEY) {
      throw new Error('Missing Supabase config (build-time VITE_* vars — see src/config.ts).');
    }
    _supabase = createClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY, {
      realtime: { params: { eventsPerSecond: 5 } }
    });
  }
  return _supabase;
}

export type BasicInfoData = {
  seeker_email: string;
  seeker_over_18: 'Yes' | 'No';
  location_postcode: string;
  seeker_gender: string; // First selected gender for backwards compatibility
  seeker_genders?: string[]; // Array of all selected genders (multi-select)
  seeker_full_name?: string;
  seeker_phone?: string;
};

export type ExperiencePreferencesData = {
  experience_types: string[];
  experience_types_other?: string;
  medicine_options_comfort: string[];
  support_types: string[];
  travel_willingness: string[];
  travel_distance?: string;
  focus_preferences?: Record<string, any>;
  focus_categories?: string[];
  focus_special_other_text?: string;
  prefer_specialist_in_groups?: string[];
  religion_alignment_important?: 'Yes' | 'No';
  seeker_beliefs?: string[];
  seeker_beliefs_other_text?: string;
};

export type MedicalAndFinalData = {
  currently_on_meds: 'Yes' | 'No';
  // clinical_diagnoses: string[]; // Comentado temporalmente - removido del flujo de onboarding
  budget_band: string;
  additional_info?: string;
  referral_source?: string;
};

type DirectConnectPayload = {
  active: boolean;
  guideId?: string;
  userId?: string;
};

export class SeekerOnboardingService {
  /**
   * Save basic info data (first checkpoint)
   */
  static async saveBasicInfo(
    seekerId: string,
    sessionId: string,
    data: BasicInfoData
  ) {
    const directConnect = this.getDirectConnectPayload();
    const attribution = getStoredAttribution();
    const result = await this.callOnboardingEdgeFunction<any>(
      'onboarding-basic',
      {
        seekerId,
        sessionId,
        data,
        directConnect,
        attribution,
      },
      'save your basic info'
    );

    // Also save to localStorage for matching service
    const localData = { seeker_id: seekerId, ...data };
    localStorage.setItem('basic_info_data', JSON.stringify(localData));
    console.log('✅ Basic info saved to localStorage for matching');

    return result;
  }

  /**
   * Save experience preferences data (second checkpoint)
   */
  static async saveExperiencePreferences(
    seekerId: string,
    sessionId: string,
    data: ExperiencePreferencesData
  ) {
    const result = await this.callOnboardingEdgeFunction<any>(
      'onboarding-experience',
      {
        seekerId,
        sessionId,
        data
      },
      'save your experience preferences'
    );

    // Also save to localStorage for matching service
    const localData = { seeker_id: seekerId, ...data };
    localStorage.setItem('experience_preferences_data', JSON.stringify(localData));
    console.log('✅ Experience preferences saved to localStorage for matching');

    return result;
  }

  /**
   * Save medical and final data (last checkpoint)
   */
  static async saveMedicalAndFinal(
    seekerId: string,
    sessionId: string,
    data: MedicalAndFinalData
  ) {
    const result = await this.callOnboardingEdgeFunction<any>(
      'onboarding-medical',
      {
        seekerId,
        sessionId,
        data
      },
      'save your final onboarding information'
    );

    // Also save to localStorage for matching service
    const localData = { seeker_id: seekerId, ...data };
    localStorage.setItem('medical_and_final_data', JSON.stringify(localData));
    console.log('✅ Medical and final data saved to localStorage for matching');

    // Note: The seeker mapping to auth.users will be created when user registers/logs in
    console.log('✅ Onboarding complete. Seeker mapping will be created during authentication.');

    return result;
  }

  /**
   * Get complete onboarding data for a seeker
   */
  static async getOnboardingData(seekerId: string, sessionId: string) {
    const { data, error } = await getSupabaseClient()
      .from('seeker_onboarding_complete')
      .select('*')
      .eq('seeker_id', seekerId)
      .eq('session_id', sessionId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
      console.error('Error fetching onboarding data:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get the current onboarding status
   */
  static async getOnboardingStatus(seekerId: string, sessionId: string) {
    const data = await this.getOnboardingData(seekerId, sessionId);
    return (data as any)?.onboarding_status || 'not_started';
  }

  /**
   * Create a new onboarding session
   */
  static createSessionId(): string {
    return crypto.randomUUID();
  }

  /**
   * Create a new ghost seeker ID (before auth)
   */
  static createGhostSeekerID(): string {
    return crypto.randomUUID();
  }

  /**
   * Create a real Supabase auth user for the seeker and send the email
   * verification code. The backend edge function:
   *  - calls supabase.auth.signUp with the email stored in seeker_basic_info
   *  - links the ghost seeker_id to the new auth user (seekers, profiles, basic_info)
   *  - triggers the confirmation email (contains the OTP token)
   *
   * After this resolves, the client should prompt for the OTP code and call
   * verifyEmailOtp() to finish confirmation and obtain a session.
   */
  static async createSeekerAccount(seekerId: string, password: string, marketingOptIn = false) {
    // Pass an explicit redirect URL so the Supabase confirmation email link
    // lands back on the seeker subdomain instead of whatever the project's
    // "Site URL" is set to. The URL still needs to be in the project's
    // Redirect URLs allowlist or Supabase will ignore it.
    const redirectTo = typeof window !== 'undefined'
      ? `${window.location.origin}/auth/callback`
      : undefined;

    const capiEventId = trackCapiEvent('Lead');
    const { data, error } = await getSupabaseClient().functions.invoke('create-seeker-account', {
      body: {
        seeker_id: seekerId,
        password,
        marketing_opt_in: marketingOptIn,
        redirect_to: redirectTo,
        capi_event_id: capiEventId,
      },
    });

    if (error) {
      console.error('create-seeker-account failed:', error);
      // Supabase FunctionsHttpError surfaces the response body in context
      const message = (error as any)?.context?.error || error.message || 'Unable to create your account. Please try again.';
      throw new Error(message);
    }

    if (data?.error) {
      throw new Error(data.error);
    }

    return data;
  }

  /**
   * Attempt to sign the seeker in immediately after createSeekerAccount().
   *
   * Why: staging has email confirmation disabled in the Supabase project
   * settings, so signUp returns an already-confirmed user and NO email is
   * sent. There's no OTP to verify, so the verification step would hang
   * forever. In that environment, signInWithPassword succeeds right away
   * and we can skip the verification step entirely.
   *
   * In prod (email confirmation enabled), signInWithPassword returns
   * "Email not confirmed" and we fall through to the OTP verification flow.
   *
   * Returns `{ skipVerification: true }` only when we actually established
   * a session on the browser.
   */
  static async trySignInAfterSignup(email: string, password: string): Promise<{ skipVerification: boolean }> {
    try {
      const { data, error } = await getSupabaseClient().auth.signInWithPassword({ email, password });
      if (error) {
        // "Email not confirmed" is the expected path in prod — silently fall
        // through to the verification step. Anything else, log but still
        // let the user try the verification step manually.
        console.log('Post-signup signIn did not succeed, falling through to verification:', error.message);
        return { skipVerification: false };
      }
      return { skipVerification: !!data.session };
    } catch (err) {
      console.warn('Unexpected error in post-signup signIn:', err);
      return { skipVerification: false };
    }
  }

  /**
   * Verify the email OTP code the seeker received after createSeekerAccount().
   * On success, a session is established on the browser client.
   *
   * NOTE: Requires the Supabase "Confirm signup" email template to include
   * `{{ .Token }}` so the 6-digit code is delivered alongside (or instead of)
   * the confirmation link.
   */
  static async verifyEmailOtp(email: string, token: string) {
    const { data, error } = await getSupabaseClient().auth.verifyOtp({
      email,
      token,
      type: 'signup',
    });

    if (error) {
      console.error('verifyOtp failed:', error);
      throw new Error(error.message || 'Invalid or expired code. Please try again.');
    }

    return data;
  }

  /**
   * Resend the signup confirmation email (contains the OTP code).
   */
  static async resendSignupOtp(email: string) {
    const emailRedirectTo = typeof window !== 'undefined'
      ? `${window.location.origin}/auth/callback`
      : undefined;

    const { error } = await getSupabaseClient().auth.resend({
      type: 'signup',
      email,
      options: emailRedirectTo ? { emailRedirectTo } : undefined,
    });

    if (error) {
      console.error('resend signup failed:', error);
      throw new Error(error.message || 'Unable to resend code. Please try again.');
    }
  }

  /**
   * Create actual seeker profile after onboarding completion and auth
   * This converts the ghost profile to a real profile
   */
  static async createSeekerFromOnboarding(
    ghostSeekerID: string,
    authUserID: string,
    profileID?: string
  ) {
    const { data, error } = await (getSupabaseClient() as any).rpc('create_seeker_from_onboarding', {
      ghost_seeker_id: ghostSeekerID,
      auth_user_id: authUserID,
      profile_id: profileID
    });

    if (error) {
      console.error('Error creating seeker from onboarding:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get complete onboarding data by ghost seeker ID
   */
  static async getOnboardingByGhostID(ghostSeekerID: string) {
    const { data, error } = await (getSupabaseClient() as any).rpc('get_onboarding_by_ghost_id', {
      ghost_seeker_id: ghostSeekerID
    });

    if (error) {
      console.error('Error fetching onboarding by ghost ID:', error);
      throw error;
    }

    return data?.[0] || null;
  }

  /**
   * Clear ghost profile data (call after successful profile creation)
   */
  static clearGhostProfile() {
    localStorage.removeItem('ghost_seeker_id');
    localStorage.removeItem('seeker_onboarding_session');
  }

  /**
   * Clear all onboarding-related data when starting fresh onboarding.
   * This ensures no stale data interferes with a new onboarding flow.
   *
   * NOTE: This should ONLY be called when:
   * - User clicks "Find My Perfect Guide" from landing page (fresh start)
   *
   * This should NOT be called for:
   * - Direct connect users (they have pre-populated data)
   * - Returning users continuing from email link
   * - Logged-in users
   */
  static clearOnboardingData() {
    // Clear seeker identifiers
    localStorage.removeItem('ghost_seeker_id');
    localStorage.removeItem('seeker_onboarding_session');

    // Clear checkpoint data
    localStorage.removeItem('basic_info_data');
    localStorage.removeItem('experience_preferences_data');
    localStorage.removeItem('medical_and_final_data');

    // Clear direct connect data (in case of stale data from previous flow)
    localStorage.removeItem('direct_connect_active');
    localStorage.removeItem('direct_connect_guide_id');
    localStorage.removeItem('direct_connect_identifier');
    localStorage.removeItem('direct_connect_email');
    localStorage.removeItem('direct_connect_name');
    localStorage.removeItem('direct_connect_user_id');
    localStorage.removeItem('direct_connect_match_created');

    // Clear matching result
    localStorage.removeItem('matching_result');

    // Clear create account data
    localStorage.removeItem('create_account_completed');
    localStorage.removeItem('create_account_data');

    // Clear persisted answers
    localStorage.removeItem('onboarding_answers');

    console.log('🧹 Cleared all onboarding data for fresh start');
  }

  /**
   * Helper to transform answers to database format
   */
  static transformAnswersToBasicInfo(answers: Record<string, any>): BasicInfoData {
    // Handle seeker_gender as either array (new multi-select) or string (legacy single-select)
    const genderValue = answers.seeker_gender;
    let seeker_gender: string;
    let seeker_genders: string[] | undefined;

    if (Array.isArray(genderValue)) {
      // Multi-select: store first value in seeker_gender for backwards compatibility
      seeker_genders = genderValue;
      seeker_gender = genderValue[0] || '';
    } else {
      // Legacy single-select: store as-is
      seeker_gender = genderValue || '';
      seeker_genders = genderValue ? [genderValue] : undefined;
    }

    return {
      seeker_email: answers.seeker_email,
      seeker_full_name: answers.seeker_full_name,
      seeker_phone: answers.seeker_phone,
      seeker_over_18: answers.seeker_over_18,
      location_postcode: answers.location_postcode,
      seeker_gender,
      seeker_genders
    };
  }

  static transformAnswersToExperiencePreferences(answers: Record<string, any>): ExperiencePreferencesData {
    // Collect all focus preference group data into a single object
    const focusPreferences: Record<string, string[]> = {};
    const focusGroupIds = [
      'focus_mental_health_pref',
      'focus_trauma_pref', 
      'focus_addiction_pref',
      'focus_relationships_pref',
      'focus_transitions_pref',
      'focus_growth_pref',
      'focus_special_pref'
    ];

    const normalizeOtherSelection = (values: any, otherText?: string): string[] | undefined => {
      if (!Array.isArray(values) || values.length === 0) return undefined;

      const trimmedOther = typeof otherText === 'string' ? otherText.trim() : '';

      return values
        .map(option => {
          if (typeof option !== 'string') {
            return option;
          }

          const normalized = option.trim();
          if (normalized.toLowerCase() === 'other' && trimmedOther.length > 0) {
            return trimmedOther;
          }

          return normalized;
        })
        .filter(option => typeof option === 'string' && option.length > 0) as string[];
    };

    const categoryLabels: Record<string, string> = {
      focus_mental_health_pref: 'Mental Health',
      focus_trauma_pref: 'Trauma & Recovery',
      focus_addiction_pref: 'Addiction & Behavioral',
      focus_relationships_pref: 'Relationships & Family',
      focus_transitions_pref: 'Life Transitions & Identity',
      focus_growth_pref: 'Personal Growth & Exploration',
      focus_special_pref: 'Special Topics'
    };

    const selectedCategories = new Set<string>();

    focusGroupIds.forEach(groupId => {
      if (answers[groupId] && Array.isArray(answers[groupId]) && answers[groupId].length > 0) {
        const otherText = groupId === 'focus_special_pref' ? answers.focus_special_other_text : undefined;
        const normalized = normalizeOtherSelection(answers[groupId], otherText);
        if (normalized && normalized.length > 0) {
          focusPreferences[groupId] = normalized;
          const label = categoryLabels[groupId];
          if (label) {
            selectedCategories.add(label);
          }
        }
      }
    });

    return {
      experience_types: answers.experience_types || [],
      experience_types_other: answers.experience_types_other,
      medicine_options_comfort: answers.medicine_options_comfort || [],
      support_types: answers.support_types || [],
      travel_willingness: answers.travel_willingness || [],
      travel_distance: answers.travel_distance,
      focus_preferences: Object.keys(focusPreferences).length > 0 ? focusPreferences : undefined,
      focus_categories: selectedCategories.size ? Array.from(selectedCategories) : undefined,
      focus_special_other_text: answers.focus_special_other_text,
      prefer_specialist_in_groups: answers.prefer_specialist_in_groups,
      religion_alignment_important: answers.religion_alignment_important,
      seeker_beliefs: answers.seeker_beliefs,
      seeker_beliefs_other_text: answers.seeker_beliefs_other_text
    };
  }

  static transformAnswersToMedicalAndFinal(answers: Record<string, any>): MedicalAndFinalData {
    return {
      currently_on_meds: answers.currently_on_meds,
      // clinical_diagnoses: answers.clinical_diagnoses || [], // Comentado temporalmente - removido del flujo de onboarding
      budget_band: answers.budget_band,
      additional_info: answers.additional_info,
      referral_source: answers.referral_source
    };
  }

  private static getDirectConnectPayload(): DirectConnectPayload | undefined {
    try {
      if (typeof localStorage === 'undefined') {
        return undefined;
      }

      const active = localStorage.getItem('direct_connect_active') === 'true';
      if (!active) {
        return undefined;
      }

      const payload: DirectConnectPayload = { active: true };
      const guideId = localStorage.getItem('direct_connect_guide_id');
      const userId = localStorage.getItem('direct_connect_user_id');

      if (guideId) {
        payload.guideId = guideId;
      }
      if (userId) {
        payload.userId = userId;
      }

      return payload;
    } catch {
      return undefined;
    }
  }

  private static async callOnboardingEdgeFunction<T>(
    functionName: string,
    payload: Record<string, unknown>,
    errorContext: string
  ): Promise<T> {
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(functionName, {
        body: payload
      });

      if (error) {
        console.error(`Failed to call edge function ${functionName}`, error);
        throw new Error(error.message || `Unable to ${errorContext}. Please try again.`);
      }

      return (data?.data ?? null) as T;
    } catch (err) {
      console.error(`Error calling edge function ${functionName}`, err);
      const message = err instanceof Error ? err.message : `Unable to ${errorContext}. Please try again.`;
      throw new Error(message);
    }
  }
}
